import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, FlaskConical, Dumbbell, Calculator } from 'lucide-react';
import { TOKENS, displayFont } from '../../lib/designTokens';

// CRO Audit §5.3 step 3 — the 3 paths users should choose between.
// Replaces the prior 5-CTA hero with curated tracks tied to the funnel stages.
const PATHS = [
  {
    id: 'peptides',
    icon: FlaskConical,
    eyebrow: 'Most popular',
    title: 'Peptides',
    description: 'Dosing protocols, safety, cycling and sourcing — BPC-157, TB-500, Retatrutide, GHK-Cu and 30+ more compounds.',
    metric: '40+ guides',
    href: '/peptides',
    testid: 'path-peptides',
  },
  {
    id: 'hyrox',
    icon: Dumbbell,
    eyebrow: 'Fastest growing',
    title: 'HYROX',
    description: 'Training plans, station guides, race-day strategy, gym finder for 185 HYROX-certified gyms across the US.',
    metric: '185 gyms',
    href: '/hyrox',
    testid: 'path-hyrox',
  },
  {
    id: 'tools',
    icon: Calculator,
    eyebrow: 'Interactive',
    title: 'Tools',
    description: 'Free calculators — Longevity Score, peptide dosing, HYROX race predictor, cycle tracker, body recomp.',
    metric: '12+ tools',
    href: '/tools',
    testid: 'path-tools',
  },
];

export const ThreePathsSection = () => (
  <section
    className="py-20 md:py-28 relative"
    style={{ backgroundColor: TOKENS.canvas }}
    data-testid="three-paths-section"
  >
    <div className="container-ninja">
      {/* Section header — kept short to preserve hierarchy */}
      <div className="mb-10 md:mb-14 max-w-2xl">
        <p
          className="text-[11px] uppercase tracking-[0.25em] mb-3"
          style={{ color: TOKENS.lime, ...displayFont }}
        >
          Pick your path
        </p>
        <h2
          className="text-white text-3xl md:text-4xl lg:text-5xl leading-tight"
          style={displayFont}
        >
          Where do you want to start?
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        {PATHS.map((path) => {
          const Icon = path.icon;
          return (
            <Link
              key={path.id}
              to={path.href}
              className="group relative block p-6 md:p-8 transition-all duration-300 hover:-translate-y-1"
              style={{
                backgroundColor: TOKENS.surface1,
                border: '1px solid rgba(255,255,255,0.06)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = TOKENS.limeBorder;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
              }}
              data-testid={path.testid}
            >
              {/* Eyebrow + metric badge */}
              <div className="flex items-center justify-between mb-6">
                <span
                  className="text-[10px] uppercase tracking-[0.2em]"
                  style={{ color: TOKENS.lime, ...displayFont }}
                >
                  {path.eyebrow}
                </span>
                <span
                  className="text-[10px] uppercase tracking-wider text-zinc-500"
                  style={displayFont}
                >
                  {path.metric}
                </span>
              </div>

              {/* Icon */}
              <Icon
                className="w-9 h-9 mb-5 transition-colors"
                style={{ color: TOKENS.lime }}
                strokeWidth={1.5}
              />

              {/* Title */}
              <h3
                className="text-white text-3xl md:text-4xl mb-3 group-hover:text-[#C7FA3E] transition-colors"
                style={displayFont}
              >
                {path.title}
              </h3>

              {/* Description */}
              <p className="text-zinc-400 text-sm leading-relaxed mb-6">
                {path.description}
              </p>

              {/* Arrow */}
              <div
                className="flex items-center gap-2 text-xs uppercase tracking-wider group-hover:gap-3 transition-all"
                style={{ color: TOKENS.lime, ...displayFont }}
              >
                Explore <ArrowUpRight className="w-4 h-4" />
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  </section>
);
